import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, Eye, Send, Share2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { lc } from '@/i18n/copy';
import { cn } from '@/lib/utils';
import type { SavedItem } from '@/pages/SavedMessages';
import { detectCategory, CATEGORY_ICONS } from '@/pages/SavedMessages';
import { getNudgeCTA } from '@/components/saved/nudgeCopy';
import type { Language } from '@/i18n/types';

const copy = {
  en: { from: 'From', viewMessage: 'View', sendAgain: 'Send Again', share: 'Share', anonymous: 'Someone', unavailable: 'Message no longer available', rewriting: 'Rewriting this moment…' },
  es: { from: 'De', viewMessage: 'Ver', sendAgain: 'Enviar de nuevo', share: 'Compartir', anonymous: 'Alguien', unavailable: 'Mensaje ya no disponible', rewriting: 'Reescribiendo este momento…' },
  fr: { from: 'De', viewMessage: 'Voir', sendAgain: 'Renvoyer', share: 'Partager', anonymous: 'Quelqu\'un', unavailable: 'Message plus disponible', rewriting: 'Réécriture de ce moment…' },
  pt: { from: 'De', viewMessage: 'Ver', sendAgain: 'Enviar de novo', share: 'Compartilhar', anonymous: 'Alguém', unavailable: 'Mensagem não disponível', rewriting: 'Reescrevendo este momento…' },
  ar: { from: 'من', viewMessage: 'عرض', sendAgain: 'أرسل مجدداً', share: 'مشاركة', anonymous: 'شخص ما', unavailable: 'الرسالة لم تعد متوفرة', rewriting: 'إعادة كتابة هذه اللحظة…' },
  ja: { from: '送信者', viewMessage: '見る', sendAgain: '再送信', share: '共有', anonymous: '誰か', unavailable: 'メッセージは利用できません', rewriting: 'この瞬間を書き直しています…' },
  ko: { from: '보낸 사람', viewMessage: '보기', sendAgain: '다시 보내기', share: '공유', anonymous: '누군가', unavailable: '메시지를 사용할 수 없습니다', rewriting: '이 순간을 다시 쓰는 중…' },
  id: { from: 'Dari', viewMessage: 'Lihat', sendAgain: 'Kirim Lagi', share: 'Bagikan', anonymous: 'Seseorang', unavailable: 'Pesan tidak lagi tersedia', rewriting: 'Menulis ulang momen ini…' },
  hi: { from: 'भेजने वाला', viewMessage: 'देखें', sendAgain: 'फिर से भेजें', share: 'साझा करें', anonymous: 'कोई', unavailable: 'संदेश अब उपलब्ध नहीं है', rewriting: 'इस पल को फिर से लिख रहे हैं…' },
};

const CARD_GRADIENTS: Record<string, string> = {
  classic: 'from-purple-900/80 via-indigo-900/70 to-purple-800/60',
  warmth: 'from-amber-900/80 via-orange-900/70 to-amber-800/60',
  forever: 'from-rose-900/80 via-pink-900/70 to-rose-800/60',
  courage: 'from-teal-900/80 via-emerald-900/70 to-teal-800/60',
  gratitude: 'from-green-900/80 via-emerald-900/70 to-green-800/60',
  celebration: 'from-yellow-900/80 via-amber-900/70 to-yellow-800/60',
  legacy: 'from-slate-900/80 via-zinc-900/70 to-slate-800/60',
  custom: 'from-violet-900/80 via-purple-900/70 to-violet-800/60',
};

const HEART_COLORS: Record<string, string> = {
  classic: 'text-purple-400',
  warmth: 'text-amber-400',
  forever: 'text-rose-400',
  courage: 'text-teal-400',
  gratitude: 'text-green-400',
  celebration: 'text-yellow-400',
  legacy: 'text-slate-400',
  custom: 'text-violet-400',
};

interface Props {
  item: SavedItem;
  index: number;
  language: Language;
  onUnsave: (e: React.MouseEvent, id: string) => void;
  onSendAgain: (item: SavedItem) => void;
  onShare: (item: SavedItem) => void;
}

export function SavedMessageCard({ item, index, language, onUnsave, onSendAgain, onShare }: Props) {
  const c = lc(copy, language);
  const navigate = useNavigate();
  const [hovering, setHovering] = useState(false);
  const [sendingAgain, setSendingAgain] = useState(false);
  const template = item.announcement?.template || 'classic';
  const gradient = CARD_GRADIENTS[template] || CARD_GRADIENTS.classic;
  const heartColor = HEART_COLORS[template] || HEART_COLORS.classic;
  const category = detectCategory(item);
  const categoryIcon = CATEGORY_ICONS[category];
  const nudgeText = getNudgeCTA(category, language);

  const truncateMessage = (msg: string) => msg.length <= 120 ? msg : msg.substring(0, 120).trim() + '…';

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString(language === 'en' ? 'en-US' : language, { month: 'short', day: 'numeric' });
    } catch { return dateStr; }
  };

  const getSenderLabel = () => {
    if (!item.announcement) return '';
    if (item.announcement.is_anonymous) return c.anonymous;
    return item.announcement.sender_display_name || c.anonymous;
  };

  const handleSendAgain = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSendingAgain(true);
    setTimeout(() => onSendAgain(item), 600);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -100 }}
      transition={{ duration: 0.5, delay: index * 0.06 }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      {/* Send Again transition overlay */}
      <AnimatePresence>
        {sendingAgain && (
          <motion.div
            className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.p
              className="text-white/80 text-[15px] italic font-light"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 }}
            >
              {c.rewriting}
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="group">
        <Link to={`/a/${item.announcement_slug}`} className="block">
          <div className={cn(
            "relative rounded-[20px] p-4 overflow-hidden bg-gradient-to-br border backdrop-blur-sm transition-all duration-300",
            gradient,
            hovering ? "border-white/25 shadow-lg shadow-black/20 -translate-y-0.5" : "border-white/10"
          )}>
            {/* Texture overlay */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(255,255,255,0.04)_0%,_transparent_60%)]" />

            {/* Category badge */}
            <div className="absolute top-3 right-3 text-sm opacity-60">
              {categoryIcon}
            </div>

            {/* Floating particles */}
            <div className="absolute top-4 right-10 w-1 h-1 bg-white/15 rounded-full animate-pulse" />
            <div className="absolute bottom-8 right-14 w-0.5 h-0.5 bg-white/10 rounded-full animate-pulse" style={{ animationDelay: '1s' }} />

            <div className="relative z-10">
              {/* Top row */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Heart className={cn("h-4 w-4 fill-current", heartColor)} />
                  <span className="font-bold text-white text-[15px]">
                    {item.announcement?.recipient_name || 'Message'}
                  </span>
                </div>
                <span className="text-[11px] text-white/35 font-medium">
                  {formatDate(item.created_at)}
                </span>
              </div>

              {/* Message excerpt */}
              {item.announcement ? (
                <p className="text-[13px] text-white/60 leading-[1.5] mb-3 line-clamp-3">
                  {truncateMessage(item.announcement.message)}
                </p>
              ) : (
                <p className="text-[13px] text-white/65 italic mb-3">{c.unavailable}</p>
              )}

              {/* Sender row */}
              <div className="flex items-center justify-between mb-0">
                <span className="text-[12px] text-white/65 italic">
                  {c.from} {getSenderLabel()}
                </span>
                <button
                  onClick={(e) => onUnsave(e, item.id)}
                  className="p-1.5 rounded-full hover:bg-white/10 transition-colors active:scale-[0.9]"
                  title="Remove"
                >
                  <Heart className="h-3.5 w-3.5 text-rose-400 fill-current" />
                </button>
              </div>
            </div>
          </div>
        </Link>

        {/* Dynamic nudge CTA */}
        <p className="text-center text-[11px] text-white/25 italic mt-1.5 mb-0.5">
          {nudgeText}
        </p>

        {/* Action row */}
        <div className={cn(
          "flex items-center justify-center gap-1 transition-all duration-200",
          "lg:opacity-0 lg:group-hover:opacity-100"
        )}>
          <Link to={`/a/${item.announcement_slug}`}>
            <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] text-white/50 hover:text-white/80 hover:bg-white/5 transition-all active:scale-[0.96]">
              <Eye className="h-3 w-3" /> {c.viewMessage}
            </button>
          </Link>
          <button
            onClick={handleSendAgain}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] text-white/50 hover:text-white/80 hover:bg-white/5 transition-all active:scale-[0.96]"
          >
            <Send className="h-3 w-3" /> {c.sendAgain}
          </button>
          <button
            onClick={(e) => { e.preventDefault(); onShare(item); }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] text-white/50 hover:text-white/80 hover:bg-white/5 transition-all active:scale-[0.96]"
          >
            <Share2 className="h-3 w-3" /> {c.share}
          </button>
        </div>
      </div>
    </motion.div>
  );
}
